/**
 * Профиль ученика для интерфейса: селекторы стора, сведённые чистыми
 * хелперами `profileService`.
 *
 * Хук не заводит ни нового состояния, ни новых полей в сторе — он лишь
 * пересобирает уже имеющиеся сигналы в то, что нужно рисовать (CEFR,
 * серия, приветствие, лучшее действие). Вся логика решения — в чистых
 * функциях сервиса; здесь только подписка на стор.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { streakDays } from '../services/forecastService';
import {
  cefrLabel,
  greeting,
  nextBestAction,
  type NextAction,
} from '../services/profileService';
import { useUserStore } from '../store/userStore';

/** Всё, что интерфейсу нужно знать об ученике прямо сейчас. */
export interface Profile {
  readonly level: number;
  readonly cefr: string;
  readonly xp: number;
  readonly tickets: number;
  readonly streak: number;
  readonly greeting: string;
  readonly next: NextAction;
}

export function useProfile(): Profile {
  // Язык интерфейса в зависимостях: приветствие пересобирается при его смене.
  const { i18n } = useTranslation();
  const level = useUserStore((state) => state.level);
  const xp = useUserStore((state) => state.xp);
  const tickets = useUserStore((state) => state.tickets);
  const progressLog = useUserStore((state) => state.progressLog);
  const activeVocabulary = useUserStore((state) => state.activeVocabulary);
  const completedLessonIds = useUserStore((state) => state.completedLessonIds);
  const userName = useUserStore((state) => state.userName);

  return useMemo(() => {
    // «Сейчас» берётся в момент пересчёта, а не хранится состоянием:
    // серия и приветствие пересчитываются при любом изменении профиля и
    // не застревают на времени открытия окна.
    const now = new Date();
    const streak = streakDays(progressLog, now);

    return {
      level,
      cefr: cefrLabel(level),
      xp,
      tickets,
      streak,
      greeting: userName === null ? greeting(now) : i18n.t('greeting.withName', { greeting: greeting(now), name: userName }),
      next: nextBestAction({
        completedLessonIds,
        activeVocabulary,
        // Программа линейная: следующий урок всегда ищется на карте.
        nextLessonRoute: '/map',
      }),
    };
  }, [level, xp, tickets, progressLog, activeVocabulary, completedLessonIds, i18n.language, userName]);
}
